// SNIGJ - Auth Context

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../lib/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    // Initialize user from localStorage immediately
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        return JSON.parse(storedUser);
      } catch {
        return null;
      }
    }
    return null;
  });
  const [loading, setLoading] = useState(true);
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('snigj_language') || 'fr';
  });

  const checkAuth = useCallback(async () => {
    try {
      const storedUser = localStorage.getItem('user');
      const token = localStorage.getItem('auth_token');
      
      if (storedUser && token) {
        // User already set from initial state, just verify with backend
        try {
          const response = await authAPI.getMe();
          setUser(response.data);
          localStorage.setItem('user', JSON.stringify(response.data));
        } catch (err) {
          // Token invalid, clear storage
          localStorage.removeItem('auth_token');
          localStorage.removeItem('user');
          setUser(null);
        }
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error('Auth check error:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (emailOrCredentials, password) => {
    try {
      // Support both (email, password) and ({email, password}) formats
      const credentials = typeof emailOrCredentials === 'string' 
        ? { email: emailOrCredentials, password } 
        : emailOrCredentials;
      
      const response = await authAPI.login(credentials);
      const { access_token, user: userData, module_access, custom_permissions } = response.data;
      
      // Attach module_access to user data for sidebar filtering
      const enrichedUser = { ...userData, module_access: module_access || [], custom_permissions: custom_permissions || [] };
      
      localStorage.setItem('auth_token', access_token);
      localStorage.setItem('user', JSON.stringify(enrichedUser));
      setUser(enrichedUser);
      
      return { success: true, user: enrichedUser };
    } catch (error) {
      console.error('Login error:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  const register = async (userData) => {
    const response = await authAPI.register(userData);
    const { access_token, user: newUser } = response.data;
    
    localStorage.setItem('auth_token', access_token);
    localStorage.setItem('user', JSON.stringify(newUser));
    setUser(newUser);
    
    return newUser;
  };

  const processOAuthSession = async (sessionId) => {
    const response = await authAPI.processSession(sessionId);
    const userData = response.data;
    
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    
    return userData;
  };

  // Login with Google OAuth credential
  const loginWithGoogle = async (credential) => {
    try {
      const response = await authAPI.googleLogin(credential);
      const { access_token, user: userData } = response.data;
      
      localStorage.setItem('auth_token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      
      return { success: true, user: userData };
    } catch (error) {
      console.error('Google login error:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Google login failed' 
      };
    }
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
    
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
    setUser(null);
  };

  const updateLanguage = (lang) => {
    setLanguage(lang);
    localStorage.setItem('snigj_language', lang);
    if (user) {
      setUser({ ...user, language: lang });
    }
  };

  const hasRole = (requiredRoles) => {
    if (!user) return false;
    if (typeof requiredRoles === 'string') {
      return user.role === requiredRoles;
    }
    return requiredRoles.includes(user.role);
  };

  const hasClearance = (requiredLevel) => {
    if (!user) return false;
    const levels = ['PUBLIC', 'RESTRICTED', 'CONFIDENTIAL', 'SECRET', 'TOP_SECRET'];
    const userLevel = levels.indexOf(user.clearance_level || 'PUBLIC');
    const required = levels.indexOf(requiredLevel);
    return userLevel >= required;
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      language,
      login,
      loginWithGoogle,
      register,
      processOAuthSession,
      logout,
      updateLanguage,
      hasRole,
      hasClearance,
      checkAuth
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
